import { merchantIcons } from '@assets/icons';
import { getMerchantPhoneLoginHero } from '@assets/illustrations';
import { getMerchantDineSavvyLogo } from '@assets/logos';
import { otpSchema } from '@common/validators';
import { toast } from '@components/Toast';
import env from '@config/env';
import { ROUTE_PATHS } from '@config/routes';
import AuthBackButton from '@modules/merchant/authentication/components/AuthBackButton';
import AuthFormPanel from '@modules/merchant/authentication/components/AuthFormPanel';
import AuthHeroPanel from '@modules/merchant/authentication/components/AuthHeroPanel';
import AuthSplitLayout from '@modules/merchant/authentication/components/AuthSplitLayout';
import OtpInputField from '@modules/merchant/authentication/components/OtpInputField';
import ResendOtpTimer from '@modules/merchant/authentication/components/ResendOtpTimer';
import {
  getRouteForMerchantConfigDestination,
  resolveMerchantConfigDestination,
} from '@modules/merchant/utils/resolveMerchantConfigDestination';
import { verifyOtp, resendMerchantOtp } from '@redux/auth/authSlice';
import { fetchAppConfig } from '@redux/merchant/merchantSlice';
import { setProfileCompleted } from '@redux/onboarding/onboardingSlice';
import { setPermissions } from '@redux/permission/permissionSlice';
import { Formik, Form } from 'formik';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

const formatPhoneDisplay = (phone) => {
  if (phone == null || phone === '') return '';
  const digits = String(phone).replace(/\D/g, '');
  const national = digits.startsWith('1') && digits.length > 10 ? digits.slice(1) : digits;
  if (national.length === 10) {
    return `+1 ${national.slice(0, 5)} ${national.slice(5)}`;
  }
  return `+1 ${phone}`;
};

const VerifyOtpPage = memo(() => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, pendingPhone, otpCooldownSeconds } = useSelector((state) => state.auth);
  const heroImage = getMerchantPhoneLoginHero();
  const dineSavvyLogo = getMerchantDineSavvyLogo();

  const formattedPhone = useMemo(() => formatPhoneDisplay(pendingPhone), [pendingPhone]);

  useEffect(() => {
    document.title = `Verify Code | ${env.APP_NAME}`;
  }, []);

  const handleSubmit = async (values) => {
    try {
      const result = await dispatch(verifyOtp({ otp: values.otp })).unwrap();
      const data = result?.data || result;
      dispatch(
        setPermissions(
          data?.user?.permissions || [
            'dashboard.view',
            'profile.view',
            'settings.view',
            'users.view',
          ],
        ),
      );
      toast.success('OTP verified successfully');

      const isNewUser = Boolean(data?.isNewUser ?? data?.user?.isNewUser);
      const hasApprovedBusiness = Boolean(
        data?.hasApprovedBusiness ?? data?.user?.hasApprovedBusiness,
      );

      if (isNewUser && !hasApprovedBusiness) {
        dispatch(setProfileCompleted(false));
        // Replace so browser back cannot return to verify-otp
        navigate(ROUTE_PATHS.CREATE_PROFILE, { replace: true });
        return;
      }

      if (!isNewUser) {
        dispatch(setProfileCompleted(true));

        try {
          const config = await dispatch(fetchAppConfig()).unwrap();
          const route =
            getRouteForMerchantConfigDestination(resolveMerchantConfigDestination(config)) ||
            ROUTE_PATHS.SETUP_GUIDE;
          navigate(route, { replace: true });
        } catch {
          navigate(ROUTE_PATHS.SETUP_GUIDE, { replace: true });
        }
        return;
      }

      dispatch(setProfileCompleted(true));
      navigate(ROUTE_PATHS.SETUP_GUIDE, { replace: true });
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'OTP verification failed');
    }
  };

  const handleEditPhone = useCallback(() => {
    navigate(ROUTE_PATHS.LOGIN);
  }, [navigate]);

  const handleResendOtp = useCallback(async () => {
    if (!pendingPhone) {
      toast.error('Phone number is missing. Please go back and enter it again.');
      throw new Error('Missing pending phone');
    }

    const result = await dispatch(resendMerchantOtp(String(pendingPhone))).unwrap();
    const payload = result?.data || result;
    return payload?.cooldownSeconds;
  }, [dispatch, pendingPhone]);

  return (
    <AuthSplitLayout>
      <AuthHeroPanel src={heroImage} alt="Fine dining experience" />

      <AuthFormPanel>
        <img
          src={dineSavvyLogo}
          alt="Dine Savvy"
          className="h-12 w-auto shrink-0 md:h-[60px] desktop:h-20"
          width={181}
          height={80}
        />

        <div className="flex w-full flex-1 flex-col justify-center gap-page">
          <div className="flex w-full flex-col gap-0.75 desktop:gap-1">
            <AuthBackButton />

            <h1 className="desktop:text-[32px] text-[24px] font-semibold leading-tight text-merchant-text-primary">
              Verify Code
            </h1>

            <div className="flex flex-col gap-1 md:flex-row md:flex-wrap md:items-center md:gap-1">
              <p className="desktop:text-[16px] text-sm font-medium leading-normal text-merchant-text-secondary">
                Enter the 6-digit code sent to
                <span className="hidden md:inline"> {formattedPhone || 'your phone'}</span>
              </p>
              <div className="flex items-center gap-0.75 desktop:gap-1">
                <span className="desktop:text-[16px] text-sm font-medium leading-normal text-merchant-text-secondary md:hidden">
                  {formattedPhone || 'your phone'}
                </span>
                <button
                  type="button"
                  onClick={handleEditPhone}
                  aria-label="Edit phone number"
                  className="inline-flex icon-md shrink-0 cursor-pointer items-center justify-center transition-opacity hover:opacity-70"
                >
                  <img src={merchantIcons.edit2} alt="" className="icon-md" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>

          <Formik
            initialValues={{ otp: '' }}
            validationSchema={otpSchema}
            validateOnChange
            validateOnBlur={false}
            onSubmit={handleSubmit}
          >
            {({ values, isSubmitting }) => (
              <Form className="flex w-full flex-col gap-form">
                <OtpInputField />
                <button
                  type="submit"
                  disabled={values.otp.length !== 6 || isSubmitting || loading}
                  className="w-full desktop:min-h-11 min-h-8 cursor-pointer rounded-full bg-merchant-action px-(--space-section) py-2.5 desktop:py-3 desktop:text-[16px] text-sm font-semibold leading-3.75 desktop:leading-5 text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading ? 'Verifying...' : 'Continue'}
                </button>
                <ResendOtpTimer initialSeconds={otpCooldownSeconds} onResend={handleResendOtp} />
              </Form>
            )}
          </Formik>
        </div>
      </AuthFormPanel>
    </AuthSplitLayout>
  );
});

VerifyOtpPage.displayName = 'VerifyOtpPage';

export default VerifyOtpPage;
